import parse from "html-react-parser"
import { motion } from "framer-motion"
import styled from "@emotion/styled"
import hash from "@emotion/hash"

import { useConsole } from "contexts/Console"

import { Paragraph as ParagraphClass } from "css/classnames"
import { legend110, bold, headline40, light, surtitle50 } from "css/text"

import Link from "components/link/Link"
import { PopinButton } from "components/components"
import VideoLauncher from "components/video-launcher/VideoLauncher"
import { contrastMode } from "css/a11y"
import getMediaQuery from "css/breakpoints"

const isBold = `b${hash("bold-paragraph")}`

const ctas = {
  link: LinkCTA,
  popin: PopinCTA,
  video_player_cld: VideoAmpCTA,
}

const Root = styled(motion.div)`
  grid-column: 2 / span 6;
  grid-row: 3;
  z-index: 0;

  height: fit-content;

  ${getMediaQuery("m")} {
    grid-column: 2 / span 7;
  }

  .norollo & {
    align-self: end;
  }
`

const Kicker = styled.p`
  ${surtitle50}
  position: relative;
  grid-column: 2 / span 5;
  grid-row: 3;
  color: rgb(var(--subheadcolor));
  ${contrastMode}
`

const Title = styled.h2`
  ${headline40}
  position: relative;
  grid-column: 2 / span 5;
  grid-row: 3;
  color: rgb(var(--subheadcolor));
  margin-block-end: 12px;
  ${contrastMode}
`

const SubTitle = styled.p`
  ${legend110}
  ${bold}
  position: relative;
  grid-column: 2 / span 5;
  grid-row: 3;
  color: rgb(var(--subheadcolor));
  margin-block-end: 20px;
  ${contrastMode}
`

const P = styled.p`
  ${legend110}
  ${light}
  position: relative;
  z-index: 0;
  color: rgb(var(--pure-white));
  grid-column: 2 / span 5;
  grid-row: 4;
  ${contrastMode}

  &.${isBold} {
    ${bold}
  }

  &:last-of-type {
    margin-block-end: 20px;
  }
  display: none;

  ${getMediaQuery("m")} {
    display: block;
  }
`

function LinkCTA({ describedBy, ...link }) {
  const console = useConsole()

  return (
    <Link {...link} aria-describedby={describedBy}>
      {link.label}
    </Link>
  )
}

function PopinCTA({ describedBy, ...popin_button }) {
  const console = useConsole()

  return <PopinButton {...popin_button} icon='plus' describedBy={describedBy} />
}

function VideoAmpCTA({ style, describedBy, ...props }) {
  const console = useConsole()

  return <VideoLauncher style={style} icon='play' video_player_cld={props} />
}

function CTA({ cta, describedBy }) {
  const console = useConsole()

  const CTAComp = ctas[cta[0]]

  return <CTAComp {...cta[1]} describedBy={describedBy} />
}

function SubHeading({ kicker, title, subtitle, sub_heading_color, ...props }) {
  const console = useConsole()
  console.verbose("\n•••>>> SubHeading", props)

  return (
    <>
      {kicker ? <Kicker>{parse(kicker)}</Kicker> : null}
      {title ? <Title>{parse(title)}</Title> : null}
      {subtitle ? <SubTitle>{parse(subtitle)}</SubTitle> : null}
    </>
  )
}

export default function TextBlock({ chapo, variants, paragraph_blocks, sub_heading, sub_heading_color, cta }) {
  const console = useConsole()
  return (
    <Root className='dark-theme' variants={variants} style={sub_heading_color ? { "--subheadcolor": sub_heading_color } : null}>
      <SubHeading {...sub_heading} sub_heading_color={sub_heading_color} />
      {/* {chapo ? <Chapo id='cover-chapo'>{chapo}</Chapo> : null} */}
      {paragraph_blocks?.block?.length
        ? paragraph_blocks?.block?.map(({ paragraph, is_bold }, i) => (
            <P key={i} className={`${ParagraphClass} ${is_bold ? isBold : ""}`}>
              {parse(paragraph)}
            </P>
          ))
        : null}
      {cta.length ? <CTA cta={Object.entries(cta[0])[0]} describedBy='cover-chapo' /> : null}
    </Root>
  )
}
